import React, { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { cloneDeep, isArray } from 'lodash';
import PlaceholderGrid from './PlaceholderGrid';
import FilterPlacement from './FilterPlacement';
import './placeholder.css';
import { usePlaceholderState, PlaceholderRow } from './context/PlaceholderProvider';
import {
  fetchAllDynamicField,
  fetchAllMultiDynamicField,
  fetchAllProductType,
  getPlaceholderData,
  clearIndividualReducer,
  clearReducer,
  publishPlaceholderAction,
  saveDraftholderProductAction,
} from './store/placeholder.actions';
import { PlaceholderIndividualDetail } from './types';
import { PlaceholderDetailsState } from './store/placeholder.reducer';
import { getSubBrandAccess } from '../SystemMasters/Store/SystemMasters.action';
import { getCounterValueNumber } from '../DevlopmentOrder/store/DevelopmentOrder.action';
import { useComponent } from '../UserSetting/UsersLibrary/UserRights';
import Chat from '../Chat/Chat';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';
import { Floaty, FloatyButton } from '../../Components/Floaty';
import { SaveIcon, CloseIcon, PublishIcon, AddIcon } from '../../bombiix-icon';
import { toasterMsg } from '../../Components/Toaster/Toaster';
import { checkArray } from '../../Components/helper';
import { localStorageService } from '../../services/localStorage.service';

type PlaceholderThunkDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

type CheckActionPermissionFn = (_userRights: Record<string, unknown>, _section: string, _module: string | null, _action: string) => boolean;

interface DetailPageReduxState {
  Users: { logInRights: unknown };
  Measuremets: { CountList: unknown };
  UserDetails: { userInfo: unknown };
  PlaceholderDetails: PlaceholderDetailsState;
}

interface DetailPageState {
  selectedIdsArray: string[];
  isSelectAll: boolean;
  draftDisable: boolean;
  publishDisable: boolean;
}

interface DynamicColumnCell {
  colId?: { _id?: string };
  selectedOpt?: { _id?: string } | null;
  valueText?: string;
}

interface DynamicColumnPayload {
  colId: string | undefined;
  valueId: string | null;
  valueText: string;
}

interface PlaceholderSaveRow {
  _id?: string;
  isAddRow?: boolean;
  isDefault?: boolean;
  placeholderStatus?: string;
  subBrandId?: unknown;
  [key: string]: unknown;
}

/**
 * Placeholder Management detail page.
 * Ports the legacy PlaceholderManagement screen to TypeScript, keeping the
 * dynamic-field/product-type init, save-draft, publish, selection totals,
 * draft/publish disabled states, close redirect, and sub-brand access gating
 * intact while swapping the mandated component library.
 */
interface PlaceholderDetailPageProps {
  embedded?: boolean;
  /** Embedded mode only: saves the parent's card-level fields (Brand/SubBrand/etc). */
  onSave?: () => Promise<void>;
  /** Embedded mode only: disables the shared Save button when the user has no add/edit rights. Read-only card fields (default "Full Range" card) must NOT disable it — grid rows are still saveable there. */
  saveDisabled?: boolean;
}

/**
 * Imperative handle exposed to an embedding parent (`AddEditCardPage`) so a
 * single Save action on the parent can also persist any pending grid draft
 * rows, without duplicating a second visible Save button.
 */
export interface PlaceholderDetailPageHandle {
  saveDraftIfPending: () => Promise<void>;
  /** Validates the grid (required fields on any pending row, duplicates) without saving. Shows the relevant error toast and returns false if invalid. */
  validateBeforeSave: () => Promise<boolean>;
}

function PlaceholderDetailPage(
  { embedded = false, onSave, saveDisabled = false }: PlaceholderDetailPageProps,
  ref: React.Ref<PlaceholderDetailPageHandle>,
): React.ReactElement {
  const dispatch = useDispatch<PlaceholderThunkDispatch>();
  const history = useHistory();
  const reducer = useSelector((reducerstate: DetailPageReduxState) => reducerstate);

  const { Users, Measuremets, UserDetails, PlaceholderDetails } = reducer;
  const { individualDetail } = PlaceholderDetails;
  const { logInRights } = Users;
  const { userInfo } = UserDetails;
  const { CountList } = Measuremets;

  const [state, setState] = useState<DetailPageState>({
    selectedIdsArray: [],
    isSelectAll: false,
    draftDisable: false,
    publishDisable: false,
  });
  const { selectedIdsArray, draftDisable, publishDisable } = state;
  const [subBrandAccess, setSubBrandAccess] = useState<boolean>(false);
  useEffect(() => {
    const fetchSubBrandAccess = async () => {
      const subBrandAccessRes = await dispatch(getSubBrandAccess());
      setSubBrandAccess(subBrandAccessRes?.data || false);
    };
    fetchSubBrandAccess();
  }, [dispatch]);

  const { t } = useTranslation('common');
  const {
    placeholderState: { placeholderManagement, pageIndex, pageSize },
    setPlaceholderState,
    checkValidation,
    checkDuplicate,
    addNewRow,
    totalCount,
    placeholderId,
  } = usePlaceholderState();
  const localStorageFilter = localStorageService.get(`placeholderFilter_${placeholderId}`);
  const getAllDynamicField = () => {
    dispatch(fetchAllDynamicField());
    dispatch(fetchAllMultiDynamicField());
  };

  const getAllProductType = () => {
    const pcTypeParams = {
      isActive: true,
      tier: 'Tier1',
    };
    dispatch(fetchAllProductType(pcTypeParams));
  };
  const [userRights, setUserRights] = useState<Record<string, unknown>>({});
  useEffect(() => {
    if (logInRights) {
      setUserRights(logInRights as Record<string, unknown>);
    }
  }, [logInRights]);

  const { CheckActionPermission } = useComponent() as unknown as { CheckActionPermission: CheckActionPermissionFn };
  const PlaceholderAddEditAccess = CheckActionPermission(userRights, 'placeholder', null, 'addEdit');
  const PlaceholderDeleteAccess = CheckActionPermission(userRights, 'placeholder', null, 'delete');

  useEffect(() => {
    const fetchPlaceholderData = async () => {
      if (individualDetail) {
        setPlaceholderState((prevState) => ({
          ...prevState,
          loader: true,
        }));
        let res = null;
        const params = {
          pageIndex: 1,
          pageSize,
          isScrolled: true,
          seasonsId: individualDetail?.season?.map((item) => item._id),
          month: individualDetail?.month || [],
          year: individualDetail?.year || [],
          category1: individualDetail?.catOneId?.map((item) => item._id),
          category2: individualDetail?.catTwoId?.map((item) => item._id),
          productTypeId: individualDetail?.productType?.map((item) => item._id),
          brandId: individualDetail?.brand?.map((item) => item._id),
          subBrandId: individualDetail?.subBrand?.map((item) => item._id),
        };
        if (localStorageFilter) {
          localStorageFilter.pageIndex = pageIndex;
          localStorageFilter.pageSize = pageSize;

          res = await dispatch(getPlaceholderData(localStorageFilter));
        } else {
          res = await dispatch(getPlaceholderData(params));
        }
        await dispatch(getCounterValueNumber());
        if (res) {
          setPlaceholderState((prevState) => ({
            ...prevState,
            loader: false,
          }));
        }
      }
    };
    fetchPlaceholderData();
  }, [individualDetail]);

  const publishPlaceholder = async () => {
    setPlaceholderState((prevState) => ({
      ...prevState,
      loader: true,
    }));
    let res = null;
    const params = {
      pageIndex: 1,
      pageSize,
      isScrolled: true,
      seasonsId: individualDetail?.season?.map((item) => item._id),
      month: individualDetail?.month,
      year: individualDetail?.year,
      category1: individualDetail?.catOneId?.map((item) => item._id),
      category2: individualDetail?.catTwoId?.map((item) => item._id),
      productTypeId: individualDetail?.productType?.map((item) => item._id),
      brandId: individualDetail?.brand?.map((item) => item._id),
      subBrandId: individualDetail?.subBrand?.map((item) => item._id),
    };

    if (localStorageFilter) {
      localStorageFilter.pageIndex = pageIndex;
      localStorageFilter.pageSize = pageSize;

      res = await dispatch(getPlaceholderData(localStorageFilter));
    } else {
      res = await dispatch(getPlaceholderData(params));
    }
    await dispatch(getCounterValueNumber());
    if (res) {
      setPlaceholderState((prevState) => ({
        ...prevState,
        loader: false,
      }));
    }
  };

  const init = async () => {
    batch(async () => {
      getAllDynamicField();
      getAllProductType();
    });
  };
  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearIndividualReducer());
      dispatch(clearReducer());
    };
  }, [dispatch]);

  const totalSelection = (selectIds: string[], id: boolean) => {
    if (!id) {
      selectIds = [];
    } else {
      placeholderManagement?.forEach((ele) => {
        if (ele.placeholderStatus === 'draft' && !ele?.isDefault) {
          selectIds.push(ele?._id as string);
        }
      });
    }

    setState((prevState) => ({
      ...prevState,
      isSelectAll: selectIds.length > 0 ? id : false,
      selectedIdsArray: selectIds,
    }));
  };

  const onSubmit = async () => {
    if (selectedIdsArray?.length > 0) {
      setState((prev) => ({
        ...prev,
        publishDisable: true,
      }));
      const obj = {
        placeholderIds: selectedIdsArray,
        CountNumber: CountList,
      };
      await dispatch(publishPlaceholderAction(obj));
      totalSelection([], false);
      await init();
      await publishPlaceholder();
      setState((prev) => ({
        ...prev,
        publishDisable: false,
      }));
    }
  };

  const getDynamicColumn = (updateEle: { dynamicColumn?: unknown[] }): DynamicColumnPayload[] | undefined => {
    return updateEle?.dynamicColumn?.map((childEleRaw) => {
      const childEle = childEleRaw as DynamicColumnCell;
      if (childEle?.selectedOpt || childEle?.valueText) {
        return {
          colId: childEle?.colId?._id,
          valueId: childEle?.selectedOpt?._id ?? null,
          valueText: childEle?.valueText ? childEle?.valueText : '',
        };
      }
      return {
        colId: childEle?.colId?._id,
        valueId: null,
        valueText: '',
      };
    });
  };

  const idOf = (val: { _id?: string } | string | null | undefined): string | undefined =>
    val && typeof val === 'object' ? val._id : (val as string | undefined);

  const getMultiCustomAttribute = (updateEle: {
    multiCustomAttribute?: unknown[];
  }): Array<{ colId: string | undefined; valId: Array<string | undefined> }> | undefined => {
    return updateEle?.multiCustomAttribute?.map((childEleRaw) => {
      const childEle = childEleRaw as { colId?: { _id?: string } | string; valId?: Array<{ _id?: string } | string> };
      return {
        colId: idOf(childEle?.colId),
        valId: (childEle?.valId || []).map((val) => idOf(val)),
      };
    });
  };

  const dataManipulation = (): PlaceholderSaveRow[] => {
    const submitedData: PlaceholderRow[] = cloneDeep(placeholderManagement);

    const newAddedData = submitedData.filter((eleNew) => eleNew.isAddRow === true || eleNew.placeholderStatus === 'draft');
    const placeholderRequestArr: PlaceholderSaveRow[] = [];
    for (const updateEle of newAddedData) {
      const obj: PlaceholderSaveRow = {
        ...updateEle,
        placeholderStatus: 'draft',
        catOneId: updateEle?.catOneId?._id || null,
        catTwoId: updateEle?.catTwoId?._id || null,
        catThreeId: updateEle?.catThreeId?._id || null,
        catFourId: updateEle?.catFourId?._id || null,
        brandId: updateEle?.brandId?._id || null,
        seasonId: updateEle?.seasonId?._id || null,
        month: updateEle?.month,
        year: updateEle?.year,
        productTypeId: updateEle?.productTypeId?._id || null,
        placeholderQuantity: updateEle.placeholderQuantity ? updateEle.placeholderQuantity : 0,
        dynamicColumn: getDynamicColumn(updateEle),
        multiCustomAttribute: getMultiCustomAttribute(updateEle),
      };

      if (updateEle?.subBrandId) {
        obj.subBrandId = updateEle?.subBrandId?._id || updateEle?.subBrandId || null;
      }
      placeholderRequestArr.push(obj);
    }
    return placeholderRequestArr;
  };

  const saveCall = async (
    placeholderRequestArr: PlaceholderSaveRow[],
    checkNewRow: PlaceholderSaveRow[],
    obj: { placeholderManagement: PlaceholderSaveRow[] },
    silent = false,
  ) => {
    if (isArray(placeholderRequestArr) && checkArray(checkNewRow)) {
      setState((prev) => ({
        ...prev,
        draftDisable: true,
      }));
      const res = await dispatch(saveDraftholderProductAction(obj));
      if (res?.code === 200) {
        const params = {
          pageIndex: 1,
          pageSize,
          seasonsId: individualDetail?.season?.map((item) => item._id),
          month: individualDetail?.month,
          year: individualDetail?.year,
          category1: individualDetail?.catOneId?.map((item) => item._id),
          category2: individualDetail?.catTwoId?.map((item) => item._id),
          productTypeId: individualDetail?.productType?.map((item) => item._id),
          brandId: individualDetail?.brand?.map((item) => item._id),
          subBrandId: individualDetail?.subBrand?.map((item) => item._id),
        };
        if (localStorageFilter) {
          localStorageFilter.pageIndex = pageIndex;
          localStorageFilter.pageSize = pageSize;
          await dispatch(getPlaceholderData(localStorageFilter));
        } else {
          await dispatch(getPlaceholderData(params));
        }
      }
      setState((prev) => ({
        ...prev,
        draftDisable: false,
      }));
    } else if (!silent) toasterMsg(t('PLACEHOLDER_MANAGEMENT.PLACE_HOLDER_MANAGEMENT.THERE_IS_NO_ADDED'), 'danger');
  };

  /**
   * Validates the grid (required fields on any pending row, then duplicates)
   * without saving anything. Always shows the relevant error toast when
   * invalid — the embedding parent (`AddEditCardPage`) awaits this before
   * saving card-level fields, so an invalid row blocks the ENTIRE save
   * (card fields included), not just the grid part.
   */
  const validateGridBeforeSave = useCallback(async (): Promise<boolean> => {
    if (checkValidation(selectedIdsArray)) {
      toasterMsg(t('PLACEHOLDER_MANAGEMENT.PLACE_HOLDER_MANAGEMENT.PLEASE_REQUIRED_FIELDS'), 'danger');
      return false;
    }
    const checkUnique = await checkDuplicate(placeholderManagement);
    if (!checkUnique?.is_Valid) {
      setPlaceholderState((prevState) => ({
        ...prevState,
        duplicateExist: checkUnique.unique,
      }));
      toasterMsg(t('PLACEHOLDER_MANAGEMENT.PLACE_HOLDER_MANAGEMENT.UNIQUE_MESSAGE'), 'danger');
      return false;
    }
    return true;
  }, [checkValidation, selectedIdsArray, checkDuplicate, placeholderManagement, setPlaceholderState, t]);

  /**
   * Persists pending grid draft/new rows, assuming `validateGridBeforeSave`
   * already passed. When `silent` is true (invoked via the imperative handle
   * from the embedding card-level Save), an empty grid is a no-op rather than
   * an error — the card-level fields are saved independently by the parent,
   * so "nothing to save here" is valid.
   */
  const saveDraft = async (silent = false) => {
    const isValid = await validateGridBeforeSave();
    if (!isValid) return;

    const placeholderRequestArr = dataManipulation();
    const placeholderRequestArrNew = placeholderRequestArr?.filter((vl) => vl.isAddRow && !vl._id && !vl.isDefault);

    let placeholderToUpdateorSave: PlaceholderSaveRow[] = [];
    if (selectedIdsArray?.length > 0) {
      placeholderToUpdateorSave = placeholderRequestArr?.filter((vl) => selectedIdsArray?.includes(vl?._id as string));
    } else if (placeholderRequestArrNew?.length) {
      placeholderToUpdateorSave = placeholderRequestArrNew;
    } else {
      placeholderToUpdateorSave = placeholderRequestArr?.filter((val) => val.isAddRow && val.isDefault);
    }
    if (silent && !checkArray(placeholderToUpdateorSave)) return;

    const obj = {
      placeholderManagement: placeholderToUpdateorSave,
    };
    saveCall(placeholderRequestArr, placeholderToUpdateorSave, obj, silent);
  };

  useImperativeHandle(
    ref,
    () => ({
      saveDraftIfPending: () => saveDraft(true),
      validateBeforeSave: validateGridBeforeSave,
    }),
    [saveDraft, validateGridBeforeSave],
  );

  /**
   * Toggles a placeholder row's selection state, or clears/selects all when passed a boolean.
   *
   * Reads the previous selection from the state updater rather than the closure: AG Grid
   * does not re-render an unchanged checkbox cell, so its onChange may hold a stale
   * handleSelection reference — reading prevState keeps multi-select toggling correct.
   *
   * @param id Placeholder id to toggle, or a boolean to clear (false) / select all (true).
   */
  const handleSelection = (id: string | boolean) => {
    if (typeof id === 'boolean') {
      totalSelection([], id);
      return;
    }
    if (!id) return;
    setState((prevState) => {
      const selectIds = [...prevState.selectedIdsArray];
      const itemIndex = selectIds.findIndex((val) => val === id);
      if (itemIndex >= 0) selectIds.splice(itemIndex, 1);
      else selectIds.push(id);
      const placeholderManagamentLength = placeholderManagement?.filter((vl) => vl.placeholderStatus === 'draft' && !vl?.isDefault)?.length;
      return {
        ...prevState,
        isSelectAll: selectIds.length > 0 && selectIds.length === placeholderManagamentLength,
        selectedIdsArray: selectIds,
      };
    });
  };

  /**
   * Floating "+" handler. Appends one blank default row at the end of the grid.
   * Only one unsaved new row is allowed at a time: if an unsaved new row already
   * exists, it validates that row (highlighting required fields) instead of
   * adding another.
   */
  const handleAddNewRow = async () => {
    const rows = placeholderManagement || [];
    const hasUnsavedNew = rows.some((row) => row.isAddRow && !row._id);
    if (hasUnsavedNew) {
      checkValidation();
      return;
    }
    await addNewRow();
  };

  const handleChatError = useCallback((errorType: string, errorData: unknown) => {
    // eslint-disable-next-line no-console
    console.log('Chat error received:', errorType, errorData);
  }, []);

  const handleResetPlaceholderCards = useCallback(() => {
    init();
  }, []);

  const redirectToListingPage = async () => {
    dispatch(clearIndividualReducer());
    dispatch(clearReducer());
    history.push('/placeholder-management');
  };

  return (
    <>
      {!embedded && (
        <div className="page-title-filter sticky">
          <div className="title-container d-flex align-items-center">
            <h4 className="title">
              {t('PLACEHOLDER_MANAGEMENT.PLACE_HOLDER_MANAGEMENT.TITLE')} | {individualDetail?.name}
            </h4>
          </div>

          <div className="search-filter action-new-row">
            <div className="search-filter">
              {selectedIdsArray?.length > 0 && (
                <span className="mr-16 text-bold mb-0">
                  {selectedIdsArray?.length} {t('PLACEHOLDER_MANAGEMENT.PLACE_HOLDER_MANAGEMENT.RECORDS_SELECTED')}
                </span>
              )}
              {placeholderManagement?.length > 0 && (
                <span className="mr-16 text-bold mb-0">
                  {totalCount} {t('PLACEHOLDER_MANAGEMENT.PLACE_HOLDER_MANAGEMENT.TOTAL_RECORDS')}
                </span>
              )}

              <FilterPlacement
                setMainState={setState as unknown as React.ComponentProps<typeof FilterPlacement>['setMainState']}
                subBrandAccess={subBrandAccess}
              />
              <div className="floting-actions">
                {PlaceholderAddEditAccess && !individualDetail?.isArchived && (
                  <CustomTooltip placement="top" tooltipText={t('CARE_LABELS.LIBRARY_FILTER.ADD')}>
                    <button type="button" className="btn d-inline-flex p-0" onClick={handleAddNewRow} aria-label="Add row">
                      <AddIcon size={24} />
                    </button>
                  </CustomTooltip>
                )}
                {(PlaceholderAddEditAccess || PlaceholderDeleteAccess) && (
                  <button type="submit" className="btn d-inline-flex pt-0" onClick={() => saveDraft()} disabled={draftDisable || individualDetail?.isArchived}>
                    <SaveIcon size={24} className="mr-1" />
                  </button>
                )}
              </div>
              {(PlaceholderAddEditAccess || PlaceholderDeleteAccess) && (
                <button
                  type="submit"
                  className="d-md-none  filter-btn filter-button_delete"
                  onClick={() => saveDraft()}
                  disabled={draftDisable || individualDetail?.isArchived}
                >
                  <SaveIcon size={24} />
                </button>
              )}
              {(PlaceholderAddEditAccess || PlaceholderDeleteAccess) && !individualDetail?.isArchived && (
                <button disabled={selectedIdsArray?.length === 0 || publishDisable} type="submit" className="btn btn-titlebar" onClick={onSubmit}>
                  <span className="text-icon">{t('PLACEHOLDER_MANAGEMENT.PLACE_HOLDER_MANAGEMENT.PUBLISH')}</span>
                </button>
              )}
            </div>
            <CustomTooltip placement="top" tooltipText={t('APPROVE_REJECTS.BACK')}>
              <button onClick={redirectToListingPage} aria-label="Close" className="btn d-inline-flex" type="button">
                <CloseIcon size={24} className="cursor-pointer" />
              </button>
            </CustomTooltip>
          </div>
        </div>
      )}
      {embedded && (PlaceholderAddEditAccess || PlaceholderDeleteAccess) && !individualDetail?.isArchived && (
        <Floaty position="fixed">
          {PlaceholderAddEditAccess && <FloatyButton icon={AddIcon} label={t('CARE_LABELS.LIBRARY_FILTER.ADD')} onClick={handleAddNewRow} size={32} />}
          {PlaceholderAddEditAccess && (
            <FloatyButton icon={SaveIcon} label={t('COMMON_LABEL.SAVE')} onClick={() => onSave?.()} disabled={saveDisabled || draftDisable} size={32} />
          )}
          <FloatyButton
            icon={PublishIcon}
            label={t('PLACEHOLDER_MANAGEMENT.PLACE_HOLDER_MANAGEMENT.PUBLISH')}
            onClick={onSubmit}
            disabled={selectedIdsArray?.length === 0 || publishDisable}
            size={32}
          />
        </Floaty>
      )}
      <div className="pt-24 h-100 overflow-hidden">
        <PlaceholderGrid
          handleSelection={handleSelection}
          mainState={state}
          placeholderAddEditAccess={PlaceholderAddEditAccess}
          placeholderDeleteAccess={PlaceholderDeleteAccess}
          refreshFunction={init}
          subBrandAccess={subBrandAccess}
        />
      </div>
      {individualDetail && !individualDetail?.isArchived && (
        <Chat
          moduleKey="placeholder"
          isDetailsPage
          moduleList={[individualDetail as PlaceholderIndividualDetail]}
          currentUser={userInfo}
          onChatError={handleChatError}
          onResetSamples={handleResetPlaceholderCards}
        />
      )}
    </>
  );
}

export default forwardRef(PlaceholderDetailPage);
